import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Brand } from "@/components/Brand";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login, verify2fa } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const dest = location.state?.from?.pathname || "/";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await login(email.trim(), password);
      if (r.requires_2fa) {
        setChallenge(r.challenge);
        toast.info("Enter your 2FA code to continue");
      } else {
        toast.success(`Welcome back, ${r.user.name.split(" ")[0]}`);
        navigate(dest, { replace: true });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || err.response?.data?.detail || "Invalid email or password");
    } finally {
      setBusy(false);
    }
  };

  const submit2fa = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await verify2fa(challenge, code.trim());
      navigate(dest, { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid 2FA code");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center p-6">
      <div className="w-full max-w-md hp-glass-strong rounded-[2rem] p-8 hp-fade-in">
        <div className="flex justify-center mb-7">
          <Brand size="lg" to={null} float />
        </div>
        <h1 className="text-2xl font-extrabold text-white text-center">
          Welcome back
        </h1>
        <p className="text-sm text-muted-foreground text-center mt-1">
          Split the bill. Settle up. No awkward math.
        </p>

        {!challenge ? (
          <form onSubmit={submit} className="mt-7 space-y-4">
            <div>
              <Label className="text-white">Email</Label>
              <Input
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="mt-1.5 bg-white/5 border-white/10 text-white"
              />
            </div>
            <div>
              <Label className="text-white">Password</Label>
              <Input
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="mt-1.5 bg-white/5 border-white/10 text-white"
              />
            </div>
            <Button
              data-testid="login-submit"
              type="submit"
              disabled={busy}
              className="hp-cta w-full h-11 rounded-xl font-bold text-white border-0"
            >
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </form>
        ) : (
          <form onSubmit={submit2fa} className="mt-7 space-y-4">
            <Label className="text-white">2FA code</Label>
            <Input
              data-testid="login-2fa-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="123456"
              inputMode="numeric"
              className="bg-white/5 border-white/10 text-white tracking-widest text-center"
            />
            <Button
              data-testid="login-2fa-submit"
              type="submit"
              disabled={busy}
              className="hp-cta w-full h-11 rounded-xl font-bold text-white border-0"
            >
              Verify
            </Button>
          </form>
        )}

        <p className="text-sm text-muted-foreground text-center mt-6">
          New here?{" "}
          <Link
            to="/register"
            data-testid="to-register"
            className="text-[#c084fc] font-semibold"
          >
            Create an account
          </Link>
        </p>
      </div>
    </div>
  );
}
